/**
 * Agent domain schemas — AGENT-REGISTRY-1.
 * Registry shapes for all AEGIS / ARTEMIS / Pillar / EXEC agents.
 */
import { z } from 'zod';

export const AgentTier = z.enum(['EXEC', 'PILLAR', 'ARTEMIS', 'AEGIS', 'ARGUS', 'KCC', 'OTHER']);

export const AgentLifecycleStage = z.enum([
  'registered', 'active', 'paused', 'deprecated', 'retired',
]);

export const AgentRecord = z.object({
  id: z.string(),                          // ULID
  tenantId: z.string().default('platform'), // 'platform' for cross-tenant agents
  agentKey: z.string(),                     // e.g. 'AEGIS-7', 'PILLAR-KCX', 'EXEC-43-Scholar'
  displayName: z.string(),
  tier: AgentTier,
  lifecycleStage: AgentLifecycleStage,
  modelRouting: z.object({
    primary: z.enum(['opus-4-7', 'opus-4-6', 'sonnet-4-6', 'haiku-4-5']),
    fallback: z.enum(['opus-4-7', 'opus-4-6', 'sonnet-4-6', 'haiku-4-5']).nullable(),
  }),
  capabilityRefs: z.array(z.string()).default([]),
  governance: z.object({
    wrapperPath: z.string(),
    disciplineEnforced: z.boolean().default(true),
    auditTrailRequired: z.boolean().default(true),
  }),
  patentRefs: z.array(z.string()).default([]),
  registeredAt: z.string(),               // ISO-8601
  lastObservedAt: z.string().nullable(),
  softDeleted: z.boolean().default(false),
  softDeletedAt: z.string().nullable().default(null),
});

export const AgentLifecycleEvent = z.object({
  id: z.string(),
  agentId: z.string(),
  fromStage: AgentLifecycleStage.nullable(),
  toStage: AgentLifecycleStage,
  reason: z.string(),
  actor: z.string(),
  correlationId: z.string(),
  timestamp: z.string(),
});

/** @typedef {import('zod').infer<typeof AgentRecord>} AgentRecordType */
/** @typedef {import('zod').infer<typeof AgentLifecycleEvent>} AgentLifecycleEventType */
